import {
  Conversation,
  ConversationArticle,
  ConversationContext,
  ConversationStatus,
} from '@stripe-internal/conversations-client';

import {Api} from '../api/api';
import {buildTranscript} from './buildTranscript';
import {
  isAssistantTriggerPoint,
  isDotComCsfTriggerPoint,
  isRestartableTriggerPoint,
} from './triggerPoints';
import {RequestIssueContext} from '../../../generated/types';

export const inputEnabled = (conversation: Conversation) => {
  const isEnabled =
    !conversation.context.enabledInputModes ||
    (conversation.context.enabledInputModes as string[]).includes('text') ||
    isConversationRestartable(conversation);

  return isEnabled;
};

export const isChannelChat = (conversation: Conversation) =>
  conversation.context.channel === 'chat';

export const isChannelRAC = (conversation: Conversation) =>
  conversation.context.channel === 'request_a_call';

export const attachmentsEnabled = (conversation: Conversation) => {
  return (
    isChannelChat(conversation) ||
    (conversation.context.enabledInputModes &&
      (conversation.context.enabledInputModes as string[]).includes(
        'attachments',
      ))
  );
};

// Disable interactive message elements when:
// - the conversation has ended
// - user has transitioned to live chat with an agent
// - user has requested a call
export const isMessageInteractivityEnabled = (conversation: Conversation) =>
  !conversation.hasEnded &&
  !isChannelChat(conversation) &&
  !isChannelRAC(conversation);

export const requestsEmail = (conversation: Conversation) => {
  return (
    conversation.context.intent === 'contact_support' &&
    conversation.context.requestedChannel === 'email'
  );
};

export const requestsRAC = (conversation: Conversation) => {
  return (
    conversation.context.channel === 'request_a_call' ||
    (conversation.context.intent === 'contact_support' &&
      conversation.context.requestedChannel === 'request_a_call')
  );
};

export function isAssistantContext(context: ConversationContext): boolean {
  return isAssistantTriggerPoint(context.triggerParams?.triggerPoint);
}

export function isConversationFromAssistant(
  conversation: Conversation,
): boolean {
  return isAssistantContext(conversation.context);
}

export function isDotComContext(context: ConversationContext): boolean {
  return context.audience === 'dot_com';
}

export function isDotComConversation(conversation: Conversation): boolean {
  return isDotComContext(conversation.context);
}

export function isDotComCsfContext(context?: ConversationContext): boolean {
  return isDotComCsfTriggerPoint(context?.triggerParams?.triggerPoint);
}

export function isConversationFromCsf(
  conversation?: Conversation,
  context?: ConversationContext,
): boolean {
  return isDotComCsfContext(conversation?.context || context);
}

export function isConversationRestartable(conversation: Conversation) {
  return (
    conversation.status === ConversationStatus.Inactive &&
    isRestartableTriggerPoint(conversation.context.triggerParams?.triggerPoint)
  );
}

export const userHasInteractedWithConversation = (
  conversation: Conversation,
): boolean => {
  const userMessages = Object.values(conversation.messages).filter(
    (message) => message.participant.role === 'user',
  );

  const interactiveMessages = userMessages.filter((message) => {
    // For options message, the user has only interacted if they selected an option
    if (
      message.type === 'select-options-conversation-message' ||
      message.type === 'button-options-conversation-message'
    ) {
      return !!message.selectedOptionId;
    }

    return true;
  });

  return interactiveMessages.length > 0;
};

export const isConversationEligibleForFeedback = async ({
  api,
  conversation,
}: {
  api: Api;
  conversation: Conversation;
}): Promise<boolean> => {
  if (isConversationFromAssistant(conversation)) {
    return false;
  }

  const isSelfServe = conversation.context.channel === 'bot';
  const userHasInteracted = userHasInteractedWithConversation(conversation);

  if (!isSelfServe || !userHasInteracted) {
    return false;
  }

  try {
    const feedbackData = await api.fetchFeedback({
      conversationId: conversation.id,
    });

    return (
      feedbackData.didWeResolve !== 'resolved' &&
      feedbackData.eligibleForFeedback
    );
  } catch {
    return false;
  }
};

export const isConversationEligibleForCSAT = (
  conversation: Conversation,
): boolean => {
  if (isConversationFromAssistant(conversation)) {
    return false;
  }

  const isSelfServe = conversation.context.channel === 'bot';
  const userHasInteracted = userHasInteractedWithConversation(conversation);

  return isSelfServe && userHasInteracted;
};

export function isConversationEligibleForDotComCSAT(
  conversation: Conversation,
): boolean {
  return (
    conversation.context.audience === 'dot_com' &&
    conversation.context.triggerParams?.triggerPoint !== 'dot_com_console' &&
    userHasInteractedWithConversation(conversation)
  );
}

export function shouldContextRequestPageContext(context?: ConversationContext) {
  return !!context && (isAssistantContext(context) || isDotComContext(context));
}

export function shouldConversationSendPageContext(
  conversation: Conversation,
): boolean {
  return (
    !!conversation &&
    (isConversationFromAssistant(conversation) ||
      isDotComConversation(conversation))
  );
}

export const buildUHConversationIssueContext = (
  conversation: Conversation,
): RequestIssueContext => {
  const uhContext = conversation.context.universalHelp as {
    articlesVisited?: Record<string, ConversationArticle>;
  };

  const issueContext: RequestIssueContext = {
    articlesVisited: uhContext?.articlesVisited
      ? Object.keys(uhContext.articlesVisited)
      : [],
    icTagsEncountered: [],
    transcript: buildTranscript(conversation),
  };

  return issueContext;
};
